// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable no-param-reassign */
import JSONAPIAdapter from '@ember-data/adapter/json-api';
import { isEmpty } from '@ember/utils';
import { decamelize } from '@ember/string';
import { inject as service } from '@ember/service';
import { baseConnectUrl } from '@square/dashboard-utilities/utils/url';
import { jsonPost, jsonPut } from '@square/dashboard-utilities/utils/xhr';
import { getCookie } from '@square/dashboard-utilities/utils/cookies';
const MAX_BULK_ORDER_TOKENS = 100;

// eslint-disable-next-line ember/no-classic-classes
export default JSONAPIAdapter.extend({
  init(args) {
    this._super(args);
    this.useFetch = false;
  },
  orderService: service('buyer-subs/order'),
  store: service(),
  modelName: 'subscription-order-template',
  namespace: 'v2',
  pathForType() {
    return 'orders';
  },
  findRecord(store, type, id) {
    return this.orderService.retrieve({
      orderId: id
    }).then(({
      order
    }) => order);
  },
  findMany(store, type, ids) {
    return this.orderService.batchRetrieve({
      orderIds: ids
    }).then(({
      orders
    }) => orders);
  },
  query(store, type, query) {
    // NOTE (amcconnell): Batch Retrieve Orders sets a limit of 100 orders per request
    if (query.ids.length > MAX_BULK_ORDER_TOKENS) {
      const promises = [];
      const chunk = MAX_BULK_ORDER_TOKENS;
      for (let i = 0, j = query.ids.length; i < j; i += chunk) {
        const batchIds = query.ids.slice(i, i + chunk);
        promises.push(this.orderService.batchRetrieve({
          orderIds: batchIds
        }));
      }
      return Promise.all(promises).then(responses => responses.flatMap(response => response.orders));
    } else {
      return this.orderService.batchRetrieve({
        orderIds: query.ids
      }).then(({
        orders
      }) => orders);
    }
  },
  createRecord(store, type, snapshot) {
    const data = snapshot.record.serialize();
    return this.orderService.create(data).then(({
      order
    }) => order);
  },
  /**
   * Helper function that recursively checks every attribute of an order to see what has been removed
   * @param {Object} changedAttributes an object with the keys of an Order and array values of [before, after]
   * @param {String[]} fieldsToClear the running list of fields to clear in the Order
   * @param {String} keyPrefix the nested key path to an attribute
   * @returns {String[]} a list of keys to delete from the Order
   */
  getFieldsToClear(changedAttributes, fieldsToClear = [], keyPrefix = '') {
    for (const key in changedAttributes) {
      const originalAttribute = changedAttributes[key][0];
      const newAttribute = changedAttributes[key][1];

      // Ex. .lineItems, .discounts, .serviceCharges, .taxes
      if (Array.isArray(changedAttributes[key][0])) {
        const newAttribute = changedAttributes[key][1] ?? [];
        for (const obj of originalAttribute) {
          const uid = obj.uid;
          const keyWithUid = uid ? `${keyPrefix}${decamelize(key)}[${uid}]` : `${keyPrefix}${decamelize(key)}`;
          if (newAttribute.some(newObj => newObj.uid === obj.uid)) {
            // object has been modified, iterate over keys
            const newObj = newAttribute.find(newObj => newObj.uid === obj.uid);
            const changedObject = Object.keys(obj).reduce((prev, key) => {
              prev[key] = [obj[key], newObj[key]];
              return prev;
            }, {});
            fieldsToClear = this.getFieldsToClear(changedObject, fieldsToClear, `${keyWithUid}.`);
          } else {
            fieldsToClear.push(keyWithUid);
          }
        }
      } else if (typeof originalAttribute === 'object' && Boolean(originalAttribute)) {
        // For object attributes such as money
        const uid = originalAttribute.uid;
        const keyWithUid = uid ? `${keyPrefix}${decamelize(key)}[${uid}]` : `${keyPrefix}${decamelize(key)}`;
        if (!newAttribute) {
          fieldsToClear.push(keyWithUid);
        } else {
          // Else check if any attributes of the object have been cleared
          const changedObject = Object.keys(originalAttribute).reduce((prev, key) => {
            prev[key] = [originalAttribute[key], newAttribute[key]];
            return prev;
          }, {});
          fieldsToClear = this.getFieldsToClear(changedObject, fieldsToClear, `${keyWithUid}.`);
        }
      } else {
        // For simple primitive attributes
        if (isEmpty(newAttribute)) {
          fieldsToClear.push(`${keyPrefix}${decamelize(key)}`);
        }
      }
    }
    return fieldsToClear;
  },
  updateRecord(store, type, snapshot) {
    const order = snapshot.record.serialize();
    // TODO: Why was this conditional operator not previously necessary
    const changedAttributes = snapshot.adapterOptions?.changedAttributes;
    const fieldsToClear = this.getFieldsToClear(changedAttributes);
    const urlPath = baseConnectUrl(`/v2/orders/${snapshot.id}`);
    return jsonPut(urlPath, {
      order,
      fields_to_clear: fieldsToClear
    }, {
      headers: {
        'X-CSRF-Token': getCookie('_js_csrf')
      }
    }).then(({
      order
    }) => order);
  },
  /**
   * Calls the calculate order endpoint and serializes the response
   * @param {*} data the order data to calculate
   * @returns {Promise} serialized order
   */
  calculateOrder(data) {
    const urlPath = baseConnectUrl('/v2/orders/calculate');
    return jsonPost(urlPath, {
      order: data
    }, {
      headers: {
        'X-CSRF-Token': getCookie('_js_csrf')
      }
    }).then(({
      order
    }) => {
      return this.store.normalize('subscription-order-template', order).data.attributes;
    });
  }
});